import { Cascader, Checkbox } from "antd";
import { useMemo, useCallback } from "react";
import { METRIC_PROBLEMS } from "../constant/metric-problems";
import { formatUnderscoreToTitle } from "@src/helpers/text-helper";

interface MetricDisplayInputProps {
    value?: string[][];
    onChange?: (value: string[][], selectedOptions?: any) => void;
    placeholder?: string;
    disabled?: boolean;
}

export const MetricDisplayInput: React.FC<MetricDisplayInputProps> = (props) => {
    const cascaderOptions = useMemo(() => convertMetricProblemsToCascaderOptions(METRIC_PROBLEMS), []);

    const { value, onChange, placeholder = "Select metrics", disabled = false } = props;

    // Generate all possible metric paths for "Select All" functionality
    const allMetricPaths = useMemo(() => {
        return cascaderOptions.flatMap((category) => category.children.map((metric) => [category.value, metric.value]));
    }, [cascaderOptions]);

    const isAllSelected = useMemo(() => {
        return value && value.length === allMetricPaths.length;
    }, [value, allMetricPaths]);

    const isPartialSelected = useMemo(() => {
        return value && value.length > 0 && value.length < allMetricPaths.length;
    }, [value, allMetricPaths]);

    const handleSelectAllChange = useCallback(
        (checked: boolean) => {
            if (onChange) {
                if (checked) {
                    onChange(allMetricPaths, []);
                } else {
                    onChange([], []);
                }
            }
        },
        [allMetricPaths, onChange],
    );

    const customDropdownRender = useCallback(
        (menu: React.ReactNode) => (
            <div>
                <div style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={isPartialSelected}
                        onChange={(e) => handleSelectAllChange(e.target.checked)}
                        disabled={disabled}
                    >
                        Select All
                    </Checkbox>
                </div>
                {menu}
            </div>
        ),
        [handleSelectAllChange, disabled, isAllSelected, isPartialSelected],
    );

    return (
        <Cascader
            style={{
                minWidth: 300,
            }}
            classNames={{
                popup: {
                    root: "metric-cascader-popup",
                },
            }}
            multiple
            options={cascaderOptions}
            value={value as string[][]}
            onChange={onChange as (value: string[][], selectedOptions?: any) => void}
            placeholder={placeholder}
            disabled={disabled}
            showSearch
            changeOnSelect
            maxTagCount="responsive"
            popupRender={customDropdownRender}
        />
    );
};

const convertMetricProblemsToCascaderOptions = (metricProblems: Record<string, string[]>) => {
    return Object.entries(metricProblems).map(([categoryKey, metrics]) => ({
        value: categoryKey,
        label: formatUnderscoreToTitle(categoryKey),
        children: metrics.map((metric) => ({
            value: metric,
            label: metric,
        })),
    }));
};
